class PremiumTheme {
  constructor() {
    this.bindMobileMenus();
    this.bindQuantityButtons();
    this.bindVariantSelects();
    this.bindWishlistButtons();
    this.renderWishlistPage();
  }

  bindMobileMenus() {
    document.querySelectorAll('[data-menu-toggle]').forEach((button) => {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;

      button.addEventListener('click', () => {
        const isOpen = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!isOpen));
        panel.classList.toggle('is-open', !isOpen);
      });

      panel.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          button.setAttribute('aria-expanded', 'false');
          panel.classList.remove('is-open');
        });
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-menu-toggle][aria-expanded="true"]').forEach((button) => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        button.setAttribute('aria-expanded', 'false');
        panel?.classList.remove('is-open');
      });
    });
  }

  bindQuantityButtons() {
    document.querySelectorAll('[data-quantity-button]').forEach((button) => {
      button.addEventListener('click', () => {
        const wrapper = button.closest('[data-quantity-wrapper]');
        const input = wrapper?.querySelector('input[type="number"]');
        if (!input) return;

        const currentValue = Number(input.value || input.min || 1);
        const step = button.dataset.quantityButton === 'increase' ? 1 : -1;
        const min = Number(input.min || 1);
        const nextValue = Math.max(min, currentValue + step);
        input.value = String(nextValue);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  bindVariantSelects() {
    document.querySelectorAll('[data-variant-select]').forEach((select) => {
      select.addEventListener('change', () => this.updateVariantState(select));
      this.updateVariantState(select);
    });
  }

  getWishlistItems() {
    try {
      return JSON.parse(localStorage.getItem('premium-theme-wishlist') || '[]');
    } catch (error) {
      return [];
    }
  }

  setWishlistItems(items) {
    localStorage.setItem('premium-theme-wishlist', JSON.stringify(items));
    this.updateWishlistState();
  }

  bindWishlistButtons() {
    document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      if (button.dataset.wishlistBound === 'true') return;
      button.dataset.wishlistBound = 'true';

      button.addEventListener('click', () => {
        const handle = button.dataset.productHandle;
        if (!handle) return;

        const items = this.getWishlistItems();
        const existingIndex = items.findIndex((item) => item.handle === handle);

        if (existingIndex >= 0) {
          items.splice(existingIndex, 1);
        } else {
          items.push({
            handle,
            title: button.dataset.productTitle || '',
            url: button.dataset.productUrl || '#',
            image: button.dataset.productImage || '',
            price: button.dataset.productPrice || ''
          });
        }

        this.setWishlistItems(items);
        this.renderWishlistPage();
      });
    });

    this.updateWishlistState();
  }

  updateWishlistState() {
    const items = this.getWishlistItems();
    const handles = items.map((item) => item.handle);

    document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      const isActive = handles.includes(button.dataset.productHandle);
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-label', `${isActive ? 'Remove from' : 'Add to'} wishlist`);
    });

    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      count.textContent = String(items.length);
      count.hidden = items.length === 0 && !count.closest('[data-wishlist-page]');
    });
  }

  escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  }

  renderWishlistPage() {
    const page = document.querySelector('[data-wishlist-page]');
    if (!page) return;

    const items = this.getWishlistItems();
    const count = page.querySelector('[data-wishlist-count]');
    const empty = page.querySelector('[data-wishlist-empty]');
    const grid = page.querySelector('[data-wishlist-grid]');

    if (count) count.textContent = String(items.length);
    if (empty) empty.hidden = items.length > 0;
    if (!grid) return;

    grid.hidden = items.length === 0;
    grid.innerHTML = items.map((item) => {
      const handle = this.escapeHTML(item.handle);
      const title = this.escapeHTML(item.title);
      const url = this.escapeHTML(item.url);
      const image = this.escapeHTML(item.image);
      const price = this.escapeHTML(item.price);

      return `
        <article class="product-card wishlist-card">
          <div class="product-card__media">
            <a class="product-card__media-link" href="${url}" aria-label="${title}">
              ${image ? `<img class="product-card__image" src="${image}" alt="${title}" loading="lazy">` : '<div class="placeholder-svg"></div>'}
            </a>
            <button class="product-card__wishlist is-active" type="button" data-wishlist-button data-product-handle="${handle}" data-product-title="${title}" data-product-url="${url}" data-product-image="${image}" data-product-price="${price}" aria-label="Remove from wishlist">
              <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 20.2s-7.2-4.4-9.3-8.3C1 8.7 2.8 5 6.4 5c2 0 3.4 1.1 4.2 2.3C11.4 6.1 12.8 5 14.8 5c3.6 0 5.4 3.7 3.7 6.9C16.4 15.8 12 20.2 12 20.2Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
          <div class="product-card__info">
            <h3 class="product-card__title"><a href="${url}">${title}</a></h3>
            <div class="price"><span class="price__regular">${price}</span></div>
          </div>
        </article>
      `;
    }).join('');

    this.bindWishlistButtons();
  }

  updateVariantState(select) {
    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return;

    const productSection = select.closest('[data-product-section]');
    const isAvailable = selectedOption.dataset.available === 'true';
    const price = selectedOption.dataset.price;
    const comparePrice = selectedOption.dataset.comparePrice;

    if (productSection) {
      const priceCurrent = productSection.querySelector('[data-price-current]');
      const priceCompare = productSection.querySelector('[data-price-compare]');
      const priceContainer = productSection.querySelector('[data-price-container]');
      const addButton = productSection.querySelector('[data-add-to-cart]');
      const addButtonText = productSection.querySelector('[data-add-to-cart-text]');

      if (priceCurrent && price) priceCurrent.textContent = price;
      if (priceCompare) {
        priceCompare.textContent = comparePrice || '';
        priceCompare.hidden = !comparePrice;
      }
      if (priceContainer) priceContainer.classList.toggle('price--sold-out', !isAvailable);
      if (addButton) addButton.disabled = !isAvailable;
      if (addButtonText) {
        addButtonText.textContent = isAvailable
          ? addButtonText.dataset.availableText
          : addButtonText.dataset.soldOutText;
      }
    }
  }
}

window.addEventListener('DOMContentLoaded', () => {
  new PremiumTheme();
});
